import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { getFrontendConfigLoader } from '@doxyme/config';
import { type DeepPartial } from '@doxyme/types';
import { realtimeSlice } from '../../features/realtime/realtime.slice';
import { callSlice } from '../../features/call/call.slice';
import { callTroubleshootingSlice } from '../../features/callTroubleshooting/callTroubleshooting.slice';

import { sessionSlice } from '../../features/session/session.slice';
import { apolloSessionSlice } from '../../features/apolloSession/apolloSession.slice';
import { postCallSurveySlice } from '../../features/postCallSurvey/postCallSurvey.slice';
import {
  addNotification,
  notificationsSlice,
} from '../../features/notifications/notifications.slice';
import { addAlert, alertsSlice } from '../../features/alerts/alerts.slice';
import { chatSlice } from '../../features/chat/chat.slice';
import { uiStateSlice } from '../../features/uiState/uiState.slice';
import { sessionApiService } from '../../services/sessionApi.service';
import { usersRTKQueryLayers } from '../../services/users/rtkquery/users.rtkQueryLayers';
import { institutionRTKQueryLayers } from '../../services/institutions/rtkquery/institutions.rtkQueryLayers';
import { buildBusinessServicesLoggerMiddleware } from '../../services/core/buildBusinessServicesLoggerMiddleware';
import { subscriptionsRTKSlice } from '../../services/subscriptions/rtkquery/subscriptions.slice';
import { legalsRTKSlice } from '../../services/legals/rtkquery/legals.slice';
import { userManageableFeaturesRTKSlice } from '../../services/userManageableFeatures/rtkquery/userManageableFeatures.slice';
import { storagesRTKQueryLayers } from '../../services/storages/rtkquery/storages.rtkQueryLayers';
import { fronteggUsersRTKSlice } from '../../services/frontegg/rtkquery/users.slice';
import { trackingSlice } from '../../features/tracking/tracking.slice';
import { waitingRoomSlice } from '../../features/waitingRoom/waitingRoom.slice';
import { modalsSlice } from '../../features/modals/modals.slice';
import { getLogger } from '../../libs/logger';
import { screenshareSlice } from '../../features/screenshare/screenshare.slice';
import { fileTransferSlice } from '../../features/fileTransfer/fileTransfer.slice';
import { graphQlService } from '../../services/graphql.service';
import { apiServiceErrorHandlerMiddleware } from './middleware/apiServiceErrorHandler';
import { accountSettingsSlice } from '../../features/accountSettings/accountSettings.slice';
import { dashboardUISlice } from '../../features/dashboardUi/dashboardUi.slice';
import { brandingSlice } from '../../features/branding/branding.slice';
import { institutionSlice } from '../../features/institution/institution.slice';
import { listenerMiddleware } from './middleware/listenerMiddleware';
import { captionsSlice } from '../../features/captions/captions.slice';
import { userPreferencesSlice } from '../../features/userPreferences/userPreferences.slice';
import { patientTransferSlice } from '../../features/patientTransfer/patientTransfer.slice';

const reducer = combineReducers({
  realtime: realtimeSlice.reducer,
  call: callSlice.reducer,
  callTroubleshooting: callTroubleshootingSlice.reducer,
  session: sessionSlice.reducer,
  apolloSession: apolloSessionSlice.reducer,
  postCallSurvey: postCallSurveySlice.reducer,
  notifications: notificationsSlice.reducer,
  alerts: alertsSlice.reducer,
  chat: chatSlice.reducer,
  sessionApi: sessionApiService.reducer,
  uiState: uiStateSlice.reducer,
  tracking: trackingSlice.reducer,
  modals: modalsSlice.reducer,
  ...usersRTKQueryLayers.reducers,
  ...institutionRTKQueryLayers.reducers,
  ...subscriptionsRTKSlice.reducers,
  ...legalsRTKSlice.reducers,
  ...userManageableFeaturesRTKSlice.reducers,
  ...storagesRTKQueryLayers.reducers,
  ...fronteggUsersRTKSlice.reducers,
  waitingRoom: waitingRoomSlice.reducer,
  screenshare: screenshareSlice.reducer,
  filetransfer: fileTransferSlice.reducer,
  apolloApi: graphQlService.reducer,
  accountSettings: accountSettingsSlice.reducer,
  dashboardUI: dashboardUISlice.reducer,
  branding: brandingSlice.reducer,
  institution: institutionSlice.reducer,
  captions: captionsSlice.reducer,
  userPreferences: userPreferencesSlice.reducer,
  patientTransfer: patientTransferSlice.reducer,
});

export const setupStore = (preloadedState?: DeepPartial<RootState>) => {
  return configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            addNotification.type,
            addAlert.type,
            sessionSlice.actions._update.type,
            'realtime/setPatientPicture/fulfilled',
          ],
          ignoredPaths: [
            'session.participantsMap',
            /session.allParticipantsMap\..*\.videoStream/u,
            /session.allParticipantsMap\..*\.videoElement/u,
            'realtime.error',
            /notifications.queue.*/u,
            /alerts.queue\.\d+\.subtitle/,
            /alerts.queue\.\d+\.actionText/,
            /alerts.queue\.\d+\.actionClick/,
            /alerts.queue\.\d+\.callback/,
            'userPreferences.chatMessageSound',
          ],
        },
        // don't rewrite it with array spread operator to not lose typings
        // details: https://github.com/reduxjs/redux-toolkit/issues/587#issuecomment-824927971
      })
        .prepend(listenerMiddleware.middleware)

        .concat(sessionApiService.middleware)

        .concat(usersRTKQueryLayers.middlewares)

        .concat(subscriptionsRTKSlice.middlewares)

        .concat(userManageableFeaturesRTKSlice.middlewares)

        .concat(legalsRTKSlice.middlewares)

        .concat(apiServiceErrorHandlerMiddleware)

        .concat(graphQlService.middleware)

        .concat(institutionRTKQueryLayers.middlewares)

        .concat(storagesRTKQueryLayers.middlewares)

        .concat(fronteggUsersRTKSlice.middlewares)

        .concat([buildBusinessServicesLoggerMiddleware(getLogger)]),
    // Tests supply deep-partial fixtures for ergonomics; configureStore expects
    // the (shallow) preloaded-state shape. This is the single, contained
    // assertion that bridges the two — callers never need to cast.
    preloadedState: preloadedState as Partial<RootState>,
    devTools: (getFrontendConfigLoader().get('ENV') as string) !== 'production',
  });
};

export type RootState = ReturnType<typeof reducer>;
export type AppStore = ReturnType<typeof setupStore>;

export type AppDispatch = AppStore['dispatch'];
